import { Component, input, signal, computed, inject, OnInit, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';

import { Achievement } from '../../model/achievement.model';
import { HelpNotif } from '../../model/helpNotif.model';
import { Student } from '../../model/student.model';
import { Teacher } from '../../model/teacher.model';
import { AchievementService } from '../../service/achievement.service';
import { AchievementDoneservice } from '../../service/achievementDone.service';
import { AchievementManagerService } from '../../service/achievementManager.service';
import { HelpNotifService } from '../../service/helpNotif.service';

/**
 * Composant Achievement Modal version Fuse (Standalone)
 * 
 * Affiche une modal d'achievement lorsqu'un étudiant ou professeur
 * débloque un succès (first-login, release, etc.)
 * 
 * Usage:
 * <app-achievement-modal-fuse 
 *   [slug]="'first-login'" 
 *   [student]="student" />
 * 
 * Pattern moderne: Standalone, signals, inject(), OnPush
 */
@Component({
  selector: 'app-achievement-modal-fuse',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatDialogModule,
  ],
  templateUrl: './achievement-modal-fuse.component.html',
  styleUrls: ['./achievement-modal-fuse.component.scss'],
})
export class AchievementModalFuseComponent implements OnInit {
  // Inputs
  teacher = input<Teacher | undefined>();
  student = input<Student | undefined>();
  slug = input.required<string>();

  // Services
  private achievementDoneService = inject(AchievementDoneservice);
  private achievementService = inject(AchievementService);
  private achievementManagerService = inject(AchievementManagerService);
  private helpNotifService = inject(HelpNotifService);
  private destroyRef = inject(DestroyRef);

  // Signals
  achievement = signal<Achievement | null>(null);
  help = signal<HelpNotif | null>(null);
  canFire = signal<boolean>(false);

  // Computed
  persona = computed(() => this.student() || this.teacher());
  
  title = computed(() => {
    const helpNotif = this.help();
    if (!helpNotif) return null;
    const lang = this.achievementService.getCurrentLanguage();
    return helpNotif[`title_${lang}`];
  });

  content = computed(() => {
    const helpNotif = this.help();
    if (!helpNotif) return null;
    const lang = this.achievementService.getCurrentLanguage();
    return helpNotif[`content_${lang}`];
  });

  illustration = computed(() => {
    const helpNotif = this.help();
    if (!helpNotif) return null;
    const lang = this.achievementService.getCurrentLanguage();
    return helpNotif[`illustration_file_${lang}`];
  });

  ngOnInit(): void {
    const studentValue = this.student();
    const teacherValue = this.teacher();

    if (studentValue) {
      this.getAchievement(studentValue.achievements);
    } else if (teacherValue) {
      this.getAchievement(teacherValue.achievements);
    }

    // Écouter les achievements à fire
    this.achievementManagerService.achievementToFire
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(achievements => {
        const currentAchievement = this.achievement();
        if (currentAchievement && achievements) {
          if (this.achievementManagerService.canFire(currentAchievement)) {
            this.getHelpNotif();
            this.canFire.set(true);
            this.achievementManagerService.setAchievementAsFire(currentAchievement);
          }
        }
      });
  }

  /**
   * Récupère l'achievement correspondant au slug
   */
  private getAchievement(achievements: Achievement[]): void {
    if (!achievements || achievements.length === 0) return;

    const slugValue = this.slug();
    const found = achievements.find(a => a.slug === slugValue);
    if (found) {
      this.achievement.set(found);
    }
  }

  /**
   * Récupère la notification d'aide associée à l'achievement
   */
  private getHelpNotif(): void {
    const currentAchievement = this.achievement();
    if (!currentAchievement || !currentAchievement.help_notif.length) return;

    this.helpNotifService.getHelpNotifByUrl(currentAchievement.help_notif[0])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(help => {
        if (help) {
          this.help.set(help);
        }
      });
  }

  /**
   * Marque l'achievement comme effectué (done)
   */
  private setAchievementAsDone(): void {
    const currentAchievement = this.achievement();
    if (!currentAchievement) return;

    const studentValue = this.student();
    const teacherValue = this.teacher();
    const personaValue = this.persona();

    const params: any = {
      achievement: currentAchievement.url,
    };

    if (studentValue) {
      params.content_type_model = 'student';
      params.object_id = studentValue.id;
    } else if (teacherValue) {
      params.content_type_model = 'teacher';
      params.object_id = teacherValue.id;
    }

    this.achievementDoneService.postAchievementDone(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(achievementDone => {
        currentAchievement.achievements_done = achievementDone.created_at;
        if (personaValue) {
          this.achievementService.updatePersonaAchievement(personaValue, currentAchievement);
        }
      });
  }

  /**
   * Ferme la modal et marque l'achievement comme done
   */
  close(): void {
    this.help.set(null);
    this.canFire.set(false);
    this.setAchievementAsDone();
  }
}
